// src/i18n/LanguageProvider.tsx

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import styled from 'styled-components';

import {
  createI18nAsync,
} from './locales/i18n.async';
import { he } from './locales/he';
import {
  createI18nConstants,
  type AppLang,
  type I18nConstants,
} from './locales/i18n.factory';

type ContextType = {
  lang: AppLang;
  setLang: (l: AppLang) => void;
  toggleLang: () => void;   // ⭐️ חדש
  i18n: I18nConstants;
};

const I18nContext = createContext<ContextType | null>(null);
const LANG_STORAGE_KEY = 'djeliormor.lang';
const SUPPORTED_LANGS: AppLang[] = ['he', 'en', 'ru', 'fr'];
const hebrewI18n = createI18nConstants(he);
const loadedI18n: Partial<Record<AppLang, I18nConstants>> = {
  he: hebrewI18n,
};

const LANGUAGE_LOADING_COPY: Record<AppLang, { brand: string; subtitle: string; label: string }> = {
  he: {
    brand: 'DJ אליאור מור',
    subtitle: 'טוען את האתר בעברית...',
    label: 'טוען עברית',
  },
  en: {
    brand: 'DJ Elior Mor',
    subtitle: 'Loading the site in English...',
    label: 'Loading English',
  },
  ru: {
    brand: 'DJ Elior Mor',
    subtitle: 'Загружаем сайт на русском...',
    label: 'Loading Russian',
  },
  fr: {
    brand: 'DJ Elior Mor',
    subtitle: 'Chargement du site en français...',
    label: 'Loading French',
  },
};

const LoaderOverlay = styled.div<{ $dir: 'rtl' | 'ltr' }>`
  position: fixed;
  inset: 0;
  z-index: 2147483647;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #0b0b10;
  color: #fff;
  direction: ${({ $dir }) => $dir};
  font-family: system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif;
`;

const LoaderCard = styled.div`
  width: min(520px, 92vw);
  border-radius: 20px;
  padding: 22px 18px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
  text-align: center;
  backdrop-filter: blur(10px);
`;

const LoaderSpinner = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 999px;
  margin: 0 auto 14px;
  border: 4px solid rgba(255, 255, 255, 0.2);
  border-top-color: rgba(255, 255, 255, 0.95);
  animation: language-loader-spin 0.9s linear infinite;

  @keyframes language-loader-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoaderTitle = styled.div`
  font-weight: 800;
  letter-spacing: 0;
  font-size: 18px;
  margin-bottom: 6px;
`;

const LoaderSubtitle = styled.div`
  opacity: 0.85;
  font-size: 14px;
  line-height: 1.35;
`;

function LanguageLoader({ targetLang }: { targetLang: AppLang }) {
  const copy = LANGUAGE_LOADING_COPY[targetLang];
  const dir = targetLang === 'he' ? 'rtl' : 'ltr';

  return (
    <LoaderOverlay $dir={dir} dir={dir} aria-label={copy.label} aria-live="polite" role="status">
      <LoaderCard>
        <LoaderSpinner aria-hidden="true" />
        <LoaderTitle>{copy.brand}</LoaderTitle>
        <LoaderSubtitle>{copy.subtitle}</LoaderSubtitle>
      </LoaderCard>
    </LoaderOverlay>
  );
}

function getInitialLang(): AppLang {
  try {
    const firstPathSegment = window.location.pathname.split('/').filter(Boolean)[0];
    if (firstPathSegment === 'en' || firstPathSegment === 'ru' || firstPathSegment === 'fr') {
      return firstPathSegment;
    }
  } catch {}

  try {
    const stored = window.localStorage.getItem(LANG_STORAGE_KEY);
    if (SUPPORTED_LANGS.includes(stored as AppLang)) {
      return stored as AppLang;
    }
  } catch {}

  return 'he';
}

export const LanguageProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [lang, setLangState] = useState<AppLang>(() => getInitialLang());
  const [i18n, setI18n] = useState<I18nConstants | null>(() => loadedI18n[lang] ?? null);
  const [loadingLang, setLoadingLang] = useState<AppLang | null>(() =>
    loadedI18n[lang] ? null : lang
  );

  const setLang = useCallback((nextLang: AppLang) => {
    const cached = loadedI18n[nextLang];

    if (cached) {
      setI18n(cached);
      setLoadingLang(null);
    } else {
      setLoadingLang(nextLang);
    }

    setLangState(nextLang);
  }, []);

  const toggleLang = useCallback(() => {
    const nextLang = lang === 'he' ? 'en' : lang === 'en' ? 'ru' : lang === 'ru' ? 'fr' : 'he';
    setLang(nextLang);
  }, [lang, setLang]);
  useEffect(() => {
    let cancelled = false;
    const cached = loadedI18n[lang];

    if (cached) {
      setI18n(cached);
      setLoadingLang(null);
      return () => {
        cancelled = true;
      };
    }

    createI18nAsync(lang).then((nextI18n) => {
      loadedI18n[lang] = nextI18n;
      if (!cancelled) {
        setI18n(nextI18n);
        setLoadingLang(null);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [lang]);

  // ⭐️⭐️⭐️ שינוי כיוון האתר כולו
  useEffect(() => {
    const isRTL = lang === 'he';

    const html = document.documentElement;

    html.lang = lang;
    html.dir = isRTL ? 'rtl' : 'ltr';

    // optional — class helpers
    html.classList.toggle('rtl', isRTL);
    html.classList.toggle('ltr', !isRTL);

    try {
      window.localStorage.setItem(LANG_STORAGE_KEY, lang);
    } catch {}

  }, [lang]);

  const value = useMemo(
    () => (i18n ? { lang, setLang, toggleLang, i18n } : null),
    [lang, setLang, toggleLang, i18n]
  );

  return (
    <>
      {value && (
        <I18nContext.Provider value={value}>
          {children}
        </I18nContext.Provider>
      )}
      {loadingLang && !loadedI18n[loadingLang] && <LanguageLoader targetLang={loadingLang} />}
    </>
  );
};

export const useI18n = (): ContextType => {
  const ctx = useContext(I18nContext);

  if (!ctx) {
    throw new Error(
      'useI18n must be used inside LanguageProvider'
    );
  }

  return ctx;
};
